import React, { useEffect, useState } from "react";
import RegisterStepper from "@/components/auth/RegisterStepper";
import RegisterStepCompany from "@/components/auth/RegisterStepCompany";
import RegisterStepUsers from "@/components/auth/RegisterStepUsers";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import RegisterStepPickUpLocation from "@/components/auth/RegisterStepPickUpLocation";
import {
  useCheckCompanyNameAvailability,
  useCreateCustomer,
  useRegister,
  useVerifyToken,
} from "@/api/auth.api";
import RegisterStepPayment from "@/components/auth/RegisterStepPayment";
import { RegisterFormData, RegisterSchema } from "@/types/forms/auth/registerFormData.form";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { login } from "@/stores/authStore";
import { getDefaultPageForUser } from "@/lib/roleBasedDefaultPage";
import { RolesEnum } from "@/constants/enums/roles.enum";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

const RegisterPage: React.FC = () => {
  const [activeStep, setActiveStep] = useState<number>(0);
  const { mutateAsync: registerApi } = useRegister();
  const { mutateAsync: createCustomer } = useCreateCustomer();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const { isLoading: isTokenLoading, isError: isTokenError } = useVerifyToken(
    searchParams.get("token") || "",
  );

  const { mutateAsync: checkCompanyNameAvailability } = useCheckCompanyNameAvailability();

  useEffect(() => {
    if (searchParams.get("continue")) {
      setActiveStep(3);
    }
  }, [searchParams]);

  const methods = useForm<RegisterFormData>({
    mode: "onChange",
    reValidateMode: "onChange",
    resolver: zodResolver(RegisterSchema),
    defaultValues: {
      businessName: "",
      billingEntities: [],
      admin: {
        lastName: "",
        firstName: "",
        email: "",
        mobilePhoneNumber: "",
        fixedPhoneNumber: "",
        password: "",
        confirmPassword: "",
        role: RolesEnum.SUPERADMIN,
      },
      additionalUsers: [],
      pickUpLocations: [],
      customerId: undefined,
    },
  });

  const onSubmitStepCompany = async () => {
    const billingEntities = methods.getValues("billingEntities");

    for (const index of billingEntities.keys()) {
      if (
        !(await methods.trigger(`billingEntities.${index}.companyName`)) ||
        !(await methods.trigger(`billingEntities.${index}.street`)) ||
        !(await methods.trigger(`billingEntities.${index}.postalCode`)) ||
        !(await methods.trigger(`billingEntities.${index}.city`)) ||
        !(await methods.trigger(`billingEntities.${index}.numberTVA`)) ||
        !(await methods.trigger(`billingEntities.${index}.companySiret`)) ||
        !(await methods.trigger(`billingEntities.${index}.companyEori`)) ||
        !(await methods.trigger(`billingEntities.${index}.email`)) ||
        !(await methods.trigger(`billingEntities.${index}.emailBis`))
      ) {
        return;
      }
    }

    if (!(await methods.trigger("businessName"))) {
      return;
    }

    const companyNameAvailability = await checkCompanyNameAvailability({
      name: methods.getValues("businessName"),
    });

    if (!companyNameAvailability) {
      toast.error("Le nom commercial est déjà utilisé");
      return;
    }

    setActiveStep((prevState) => prevState + 1);
    window.scrollTo(0, 0);
  };

  const onSubmitStepUsers = async () => {
    if (!(await methods.trigger("admin"))) {
      return;
    }

    setActiveStep((prevState) => prevState + 1);
    window.scrollTo(0, 0);
  };

  const onSubmitStepPickUpLocation = async () => {
    if (!(await methods.trigger("pickUpLocations"))) {
      return;
    }

    const formData = methods.getValues();

    const createdCustomer = await createCustomer({
      addressLine1: formData.billingEntities[0].street,
      city: formData.billingEntities[0].city,
      companyName: formData.businessName,
      countryCode: "FR",
      email: formData.admin.email,
      familyName: formData.businessName,
      givenName: formData.businessName,
      phoneNumber: formData.admin.mobilePhoneNumber,
      postalCode: formData.billingEntities[0].postalCode,
    });

    if (!createdCustomer.data.id) {
      return;
    }

    methods.setValue("customerId", createdCustomer.data.id);
    // Make sure to clear the local storage, it is needed as in next step form data from local storage will loaded to handle back and forth with gocardless
    localStorage.removeItem("registerFormData");
    localStorage.removeItem("registerActiveStep");

    setActiveStep((prevState) => prevState + 1);
    window.scrollTo(0, 0);
  };

  const onSubmitPayment = async (data: RegisterFormData) => {
    const billingEntities = methods.getValues("billingEntities").map((billingEntity) => ({
      companyName: billingEntity.companyName,
      street: billingEntity.street,
      postalCode: billingEntity.postalCode,
      city: billingEntity.city,
      country: billingEntity.country,
      numberTVA: billingEntity.numberTVA,
      companySiret: billingEntity.companySiret,
      companyEori: billingEntity.companyEori,
      email: billingEntity.email,
      emailBis: billingEntity.emailBis,
      paymentMethod: billingEntity.paymentMethod!.value,
      billingRequestId: billingEntity?.billingRequestId,
    }));

    const { confirmPassword: _adminConfirmPwd, ...adminPayload } = data.admin;
    const result = await registerApi({
      ...data,
      admin: adminPayload,
      additionalUsers: data.additionalUsers.map(({ confirmPassword: _cp, ...user }) => user),
      billingEntities,
      token: searchParams.get("token") || "",
    });

    await login(result.data);

    // Redirect after successful login
    if (result.data && result.data.role) {
      navigate(getDefaultPageForUser(result.data.role, result.data.isBoUser));
    } else {
      navigate("/login");
    }
  };

  const onBack = () => {
    setActiveStep((prevState) => prevState - 1);
  };

  const getMainComponent = () => {
    switch (activeStep) {
      case 0:
        return <RegisterStepCompany onSubmit={() => onSubmitStepCompany()} />;
      case 1:
        return <RegisterStepUsers onSubmit={() => onSubmitStepUsers()} onBack={onBack} />;
      case 2:
        return (
          <RegisterStepPickUpLocation
            onSubmit={() => onSubmitStepPickUpLocation()}
            onBack={onBack}
          />
        );
      case 3:
        return (
          <RegisterStepPayment onSubmit={methods.handleSubmit(onSubmitPayment)} onBack={onBack} />
        );
      default:
        return null;
    }
  };

  if (isTokenLoading) {
    return (
      <div className="flex min-h-screen w-screen">
        <div className="mx-auto flex flex-col items-center gap-4 rounded-2xl bg-white px-6 py-6 md:px-16 md:py-12 xl:max-h-[500px] xl:max-w-[600px]">
          <Loader2 className="h-10 w-10 animate-spin" />
          <h1 className="text-center text-lg font-medium text-black sm:text-xl">
            Chargement de la page...
          </h1>
        </div>
      </div>
    );
  }

  if (isTokenError) {
    return (
      <div className="bg-brand600 flex min-h-screen w-screen items-center justify-center">
        <div className="mx-4 flex flex-col items-center gap-4 rounded-2xl bg-white px-6 py-6 md:px-16 md:py-12 xl:max-h-[500px] xl:max-w-[600px]">
          <img src="/images/pelican-logo.png" className="h-24 w-24 sm:h-32 sm:w-32" />
          <div className="flex w-full flex-col items-center gap-3">
            <h1 className="w-full text-center text-lg font-medium text-black sm:text-xl">
              Erreur lors du chargement de la page
            </h1>
            <p className="w-full text-center text-sm text-gray-600">
              Le lien que vous utlisez est invalide ou a expiré. Veuillez contacter Pelican Air
              Services pour en savoir plus.
            </p>
          </div>
          <Button onClick={() => navigate("/login")}>Rediriger vers la page de connexion</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-screen">
      <div className="hidden lg:flex">
        <RegisterStepper activeStep={activeStep} />
      </div>
      <div className="flex w-screen justify-center">
        <FormProvider {...methods}>
          <div className="w-screen max-w-[700px]">{getMainComponent()}</div>
        </FormProvider>
      </div>
    </div>
  );
};

export default RegisterPage;
